import {patientNameFromRecord} from './patientNames';

const DIACRITICS_RE = /[\u0300-\u036f]/g;
const NON_ALPHANUMERIC_RE = /[^a-z0-9]+/g;
const MONTH_INDEX: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

export function normalizePatientSearchText(value: unknown): string {
  return String(value || '')
    .normalize('NFD')
    .replace(DIACRITICS_RE, '')
    .toLowerCase()
    .replace(NON_ALPHANUMERIC_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function compactDigits(value: unknown): string {
  return String(value || '').replace(/\D/g, '');
}

function uniq(values: unknown[]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const trimmed = String(value || '').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

function normalizeYear(year: number): number {
  return year < 100 ? year + (year >= 30 ? 1900 : 2000) : year;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
}

function validDateParts(
  year: number,
  month: number,
  day: number,
): DateParts | null {
  const fullYear = normalizeYear(year);
  if (
    !Number.isFinite(fullYear) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  )
    return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(fullYear, month - 1, day));
  if (
    date.getUTCFullYear() !== fullYear ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return {year: fullYear, month, day};
}

function monthNameToNumber(value: unknown): number | null {
  const key = String(value || '')
    .toLowerCase()
    .replace(/\.$/, '');
  return MONTH_INDEX[key] || null;
}

function getDateParts(value: unknown): DateParts | null {
  if (!value) return null;

  const ts = value as {toDate?: () => Date; seconds?: number};
  let dateValue: unknown = value;
  if (typeof ts.toDate === 'function') dateValue = ts.toDate();
  if (typeof ts.seconds === 'number') dateValue = new Date(ts.seconds * 1000);

  if (dateValue instanceof Date) {
    if (Number.isNaN(dateValue.getTime())) return null;
    return {
      month: dateValue.getMonth() + 1,
      day: dateValue.getDate(),
      year: dateValue.getFullYear(),
    };
  }

  const raw = String(value || '').trim();
  if (!raw) return null;

  let match = raw.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (match) {
    return validDateParts(Number(match[1]), Number(match[2]), Number(match[3]));
  }

  match = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (match) {
    return validDateParts(Number(match[3]), Number(match[1]), Number(match[2]));
  }

  match = raw.match(/^(\d{1,2})(\d{2})(\d{2}|\d{4})$/);
  if (match) {
    return validDateParts(Number(match[3]), Number(match[1]), Number(match[2]));
  }

  match = raw.match(
    /^([a-zA-Z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?[,]?\s+(\d{2}|\d{4})$/,
  );
  if (match) {
    const month = monthNameToNumber(match[1]);
    if (month) return validDateParts(Number(match[3]), month, Number(match[2]));
  }

  match = raw.match(
    /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-zA-Z]+)\.?,?\s+(\d{2}|\d{4})$/,
  );
  if (match) {
    const month = monthNameToNumber(match[2]);
    if (month) return validDateParts(Number(match[3]), month, Number(match[1]));
  }

  return null;
}

function buildDobSearchValues(dob: unknown): string[] {
  const raw = String(dob || '').trim();
  const values = [raw, normalizePatientSearchText(raw), compactDigits(raw)];
  const parts = getDateParts(dob);
  if (!parts) return uniq(values);

  const month = String(parts.month);
  const day = String(parts.day);
  const year = String(parts.year);
  const paddedMonth = month.padStart(2, '0');
  const paddedDay = day.padStart(2, '0');
  const shortYear = year.slice(-2);
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const monthLong = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    timeZone: 'UTC',
  }).format(date);
  const monthShort = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  }).format(date);

  values.push(
    `${month}/${day}/${year}`,
    `${paddedMonth}/${paddedDay}/${year}`,
    `${year}-${paddedMonth}-${paddedDay}`,
    `${month}/${day}/${shortYear}`,
    `${paddedMonth}/${paddedDay}/${shortYear}`,
    `${month}-${day}-${year}`,
    `${paddedMonth}-${paddedDay}-${year}`,
    `${month}.${day}.${year}`,
    `${paddedMonth}.${paddedDay}.${year}`,
    `${monthLong} ${day}, ${year}`,
    `${monthLong} ${day} ${year}`,
    `${monthShort} ${day}, ${year}`,
    `${monthShort} ${day} ${year}`,
    `${day} ${monthLong} ${year}`,
    `${day} ${monthShort} ${year}`,
    `${month} ${day} ${year}`,
    `${paddedMonth}${paddedDay}${year}`,
    `${month}${paddedDay}${year}`,
    `${paddedMonth}${paddedDay}${shortYear}`,
    `${month}${paddedDay}${shortYear}`,
    year,
  );

  return uniq(values);
}

function dobMatchesQuery(query: unknown, dob: unknown): boolean {
  if (!dob) return false;

  const normalizedQuery = normalizePatientSearchText(query);
  const queryDigits = compactDigits(query);
  const queryLooksDateLike = /[-/.]/.test(String(query || ''));
  const dobValues = buildDobSearchValues(dob);

  if (
    normalizedQuery &&
    dobValues.some(value =>
      normalizePatientSearchText(value).includes(normalizedQuery),
    )
  ) {
    return true;
  }

  const minDigitLength = queryLooksDateLike ? 3 : 4;
  return Boolean(
    queryDigits.length >= minDigitLength &&
    dobValues.some(value => compactDigits(value).includes(queryDigits)),
  );
}

// Patient objects arrive from several backends (Firestore, EHR cache,
// planner scaffold) with overlapping-but-different field spellings, so
// this helper accepts any patient-shaped bag.
export function patientMatchesSearch(
  query: unknown,
  patient: Record<string, any> = {},
): boolean {
  const normalizedQuery = normalizePatientSearchText(query);
  if (!normalizedQuery) return true;

  const name = patientNameFromRecord(patient);
  const firstName = name.firstName;
  const middleName = name.middleName;
  const lastName = name.lastName;
  const fullName = name.fullName || name.displayName;
  const lastFirstName = [lastName, firstName, middleName]
    .filter(Boolean)
    .join(' ');

  const searchableValues = [
    fullName,
    name.displayName,
    lastFirstName,
    firstName,
    middleName,
    lastName,
    patient.mrn,
    patient.accountNo,
    patient.account_no,
    patient.id,
    patient.ehrSourceId,
    patient.ehr_source_id,
    patient.language,
    patient.preferred_language,
  ];

  const normalizedValues = searchableValues
    .map(normalizePatientSearchText)
    .filter(Boolean);
  if (normalizedValues.some(value => value.includes(normalizedQuery)))
    return true;

  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  const normalizedName = normalizePatientSearchText(
    [fullName, lastFirstName, firstName, middleName, lastName]
      .filter(Boolean)
      .join(' '),
  );
  if (
    queryTokens.length > 1 &&
    queryTokens.every(token => normalizedName.includes(token))
  ) {
    return true;
  }

  return dobMatchesQuery(
    query,
    patient.dob || patient.date_of_birth || patient.dateOfBirth,
  );
}

export function buildPatientSearchQueries(query: unknown): string[] {
  const raw = String(query || '').trim();
  if (!raw) return [];

  const variants: string[] = [];
  const commaIndex = raw.indexOf(',');
  if (commaIndex >= 0) {
    const beforeComma = raw.slice(0, commaIndex).trim();
    const afterComma = raw.slice(commaIndex + 1).trim();

    if (beforeComma) variants.push(beforeComma);
    if (beforeComma && afterComma) {
      variants.push(`${afterComma} ${beforeComma}`);
      variants.push(`${beforeComma} ${afterComma}`);
    }
  } else {
    variants.push(raw);
  }

  const normalized = normalizePatientSearchText(raw);
  if (normalized) variants.push(normalized);

  const dateParts = getDateParts(raw);
  if (dateParts) {
    const month = String(dateParts.month);
    const day = String(dateParts.day);
    const year = String(dateParts.year);
    variants.push(
      `${month}/${day}/${year}`,
      `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`,
      `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
      `${month}/${day}/${year.slice(-2)}`,
      `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year.slice(-2)}`,
      `${month.padStart(2, '0')}${day.padStart(2, '0')}${year}`,
      ...buildDobSearchValues(raw),
    );
  }

  return uniq(variants);
}
